import React, { useEffect, useState } from "react";
import {
  ThumbsUp, ThumbsDown, MessageSquarePlus, Loader2, ExternalLink, ChevronDown, ChevronUp, X, Trash2,
} from "lucide-react";
import { fetchFactDebate, castFactStance, postFactReply } from "../lib/api";
import { api } from "../lib/api";
import { getVoterId } from "../lib/voter";
import { isAdmin, adminHeader } from "../lib/admin";
import { toast } from "sonner";

const LOCAL_NAME_KEY = "vault.comment.name";

/**
 * FactDebate — sits under a single CommunityFact and lets viewers:
 *  - Vote SUPPORT / DISPUTE on the fact (one stance per browser; click same again to clear)
 *  - Post a reply tagged with their stance, with optional source URL
 *  - See all replies inline, color-coded by stance
 * State is loaded lazily on first expand to keep the dossier page snappy.
 */
export default function FactDebate({ factId }) {
  const voterId = React.useMemo(() => getVoterId(), []);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ support_count: 0, dispute_count: 0, my_stance: null, replies: [] });
  const [admin, setAdmin] = useState(isAdmin());

  // Reply form state
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyStance, setReplyStance] = useState("support");
  const [replyText, setReplyText] = useState("");
  const [replyUrl, setReplyUrl] = useState("");
  const [replyAuthor, setReplyAuthor] = useState(() => localStorage.getItem(LOCAL_NAME_KEY) || "");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    const h = () => setAdmin(isAdmin());
    window.addEventListener("admin-changed", h);
    return () => window.removeEventListener("admin-changed", h);
  }, []);

  const load = async () => {
    if (loaded) return;
    setBusy(true);
    try {
      const d = await fetchFactDebate(factId, voterId);
      setData(d);
      setLoaded(true);
    } catch (e) {
      // 404 fact_not_found is the only realistic failure here; silently degrade.
    } finally {
      setBusy(false);
    }
  };

  // Auto-load counts on mount so users see them even before expanding
  useEffect(() => {
    let cancelled = false;
    fetchFactDebate(factId, voterId).then((d) => !cancelled && setData(d)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factId]);

  const onToggleExpanded = async () => {
    if (!expanded && !loaded) await load();
    setExpanded((v) => !v);
  };

  const vote = async (stance) => {
    if (busy) return;
    setBusy(true);
    try {
      const d = await castFactStance(factId, voterId, stance);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || posting) return;
    setPosting(true);
    try {
      const d = await postFactReply(factId, voterId, replyStance, replyText.trim(), replyAuthor.trim(), replyUrl.trim());
      setData(d);
      if (replyAuthor.trim()) localStorage.setItem(LOCAL_NAME_KEY, replyAuthor.trim());
      setReplyText("");
      setReplyUrl("");
      setReplyOpen(false);
      setExpanded(true); // ensure their new reply is visible
      toast.success(`Receipt added under [${replyStance.toUpperCase()}]`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reply failed");
    } finally {
      setPosting(false);
    }
  };

  const onDeleteReply = async (replyId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/admin/fact-replies/${replyId}`, { headers: adminHeader() });
      setData((d) => ({ ...d, replies: d.replies.filter((r) => r.id !== replyId) }));
      toast.success("Reply deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const replyCount = data.replies?.length || 0;
  const supportActive = data.my_stance === "support";
  const disputeActive = data.my_stance === "dispute";

  return (
    <div data-testid={`fact-debate-${factId}`} className="mt-3 pt-3 border-t border-white/10 print-hide-children">
      {/* Stance row + expand toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 no-print">
          <button
            type="button"
            onClick={() => vote("support")}
            disabled={busy}
            data-testid={`fact-support-${factId}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors disabled:opacity-50 ${
              supportActive
                ? "bg-[#00ff88] text-[#050505] border-[#00ff88]"
                : "text-[#00ff88] border-[#00ff88]/40 hover:bg-[#00ff88]/10"
            }`}
            title={supportActive ? "Click again to clear vote" : "Support this fact"}
          >
            <ThumbsUp size={11} /> Support · {data.support_count}
          </button>
          <button
            type="button"
            onClick={() => vote("dispute")}
            disabled={busy}
            data-testid={`fact-dispute-${factId}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors disabled:opacity-50 ${
              disputeActive
                ? "bg-[#ff3b30] text-white border-[#ff3b30]"
                : "text-[#ff3b30] border-[#ff3b30]/40 hover:bg-[#ff3b30]/10"
            }`}
            title={disputeActive ? "Click again to clear vote" : "Dispute this fact"}
          >
            <ThumbsDown size={11} /> Dispute · {data.dispute_count}
          </button>
          <button
            type="button"
            onClick={() => { setReplyOpen((v) => !v); if (!expanded) setExpanded(true); }}
            data-testid={`fact-reply-toggle-${factId}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] tracking-[0.2em] uppercase border border-white/15 text-zinc-300 hover:text-white hover:border-white/40"
          >
            {replyOpen ? <X size={11} /> : <MessageSquarePlus size={11} />}
            {replyOpen ? "Cancel" : "Add Receipt"}
          </button>
        </div>
        {replyCount > 0 && (
          <button
            type="button"
            onClick={onToggleExpanded}
            data-testid={`fact-expand-${factId}`}
            className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white no-print"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {replyCount} Receipt{replyCount === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {/* Reply composer */}
      {replyOpen && (
        <form
          onSubmit={submitReply}
          data-testid={`fact-reply-form-${factId}`}
          className="mt-3 bg-[#070707] border border-white/10 p-3 space-y-2 no-print"
        >
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">ARGUING FOR:</span>
            <button
              type="button"
              onClick={() => setReplyStance("support")}
              data-testid={`reply-stance-support-${factId}`}
              className={`px-2 py-0.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors ${
                replyStance === "support"
                  ? "bg-[#00ff88] text-[#050505] border-[#00ff88]"
                  : "text-[#00ff88] border-[#00ff88]/40"
              }`}
            >
              Support
            </button>
            <button
              type="button"
              onClick={() => setReplyStance("dispute")}
              data-testid={`reply-stance-dispute-${factId}`}
              className={`px-2 py-0.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors ${
                replyStance === "dispute"
                  ? "bg-[#ff3b30] text-white border-[#ff3b30]"
                  : "text-[#ff3b30] border-[#ff3b30]/40"
              }`}
            >
              Dispute
            </button>
          </div>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={2}
            maxLength={1500}
            placeholder={
              replyStance === "support"
                ? "Back this up — what's the receipt? Source it."
                : "Push back — what's wrong and why? Source if you can."
            }
            data-testid={`fact-reply-text-${factId}`}
            className="w-full bg-transparent border border-white/15 px-2 py-1.5 font-mono text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-white/40 resize-none"
          />
          <div className="grid sm:grid-cols-[180px_1fr] gap-2">
            <input
              type="text"
              value={replyAuthor}
              onChange={(e) => setReplyAuthor(e.target.value)}
              placeholder="Handle (optional)"
              maxLength={40}
              className="bg-transparent border border-white/15 px-2 py-1.5 font-mono text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-white/40"
            />
            <input
              type="url"
              value={replyUrl}
              onChange={(e) => setReplyUrl(e.target.value)}
              placeholder="https://source-url.com (optional)"
              className="bg-transparent border border-white/15 px-2 py-1.5 font-mono text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-white/40"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-600">
              {replyText.length} / 1500
            </span>
            <button
              type="submit"
              disabled={!replyText.trim() || posting}
              data-testid={`fact-reply-submit-${factId}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-[#050505] hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-[0.2em]"
            >
              {posting ? <Loader2 size={11} className="animate-spin" /> : null}
              Post Receipt
            </button>
          </div>
        </form>
      )}

      {/* Replies list */}
      {expanded && replyCount > 0 && (
        <ul data-testid={`fact-replies-${factId}`} className="mt-3 space-y-2">
          {data.replies.map((r) => {
            const isSupport = r.stance === "support";
            return (
              <li
                key={r.id}
                className={`pl-3 py-2 border-l-2 bg-[#070707] ${
                  isSupport ? "border-l-[#00ff88]" : "border-l-[#ff3b30]"
                }`}
              >
                <div className="flex items-center justify-between mb-1 font-mono text-[9px] tracking-[0.25em] text-zinc-500 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 border ${
                      isSupport
                        ? "text-[#00ff88] border-[#00ff88]/40 bg-[#00ff88]/5"
                        : "text-[#ff3b30] border-[#ff3b30]/40 bg-[#ff3b30]/5"
                    }`}>
                      [{r.stance.toUpperCase()}]
                    </span>
                    <span className="text-zinc-400">{r.author || "Anon"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span>{(r.created_at || "").slice(0, 10)}</span>
                    {admin && (
                      <button
                        onClick={() => onDeleteReply(r.id)}
                        data-testid={`admin-delete-reply-${r.id}`}
                        className="text-zinc-500 hover:text-[#ff3b30] no-print"
                        aria-label="Delete reply"
                      >
                        <Trash2 size={10} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{r.text}</p>
                {r.source_url && (
                  <a
                    href={r.source_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 mt-1 font-mono text-[9px] tracking-wider text-blue-400 hover:text-blue-300 break-all"
                  >
                    <ExternalLink size={9} />
                    {r.source_url}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
